import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button, Card, Combobox, Input, Modal, Select, Spinner } from '@/components/ui';
import {
  createMovement,
  type CompensationType,
  type CreateMovementInput,
  type MovementStatus,
  type MovementType,
} from '@/api/services/movements.service';
import {
  getCompanyEmploymentProfile,
  listCompanyEmployments,
} from '@/api/services/employment.service';
import { listHrMovements, type HrPersonRef } from '@/api/services/hrWorkspace.service';
import { listHrStructure, type HrStructureKind } from '@/api/services/hr-structure.service';
import { EmployeePicker } from '@/features/hr/components/people/EmployeePicker';
import { searchCompanyPeople } from '@/features/hr/components/cases/casePeople';
import type { HrAreaProps } from '@/features/hr/components/hrShared';
import { useOpenFromQuery } from '@/hooks/useOpenFromQuery';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { usePermissions } from '@/hooks/usePermissions';
import { MovementStatusBadge } from './MovementStatusBadge';
import { MovementDetail } from './MovementDetail';
import {
  formatCompensation,
  MOVEMENT_TYPE_LABELS,
  movementChangeSummary,
  movementTypeLabel,
} from './movementFormat';

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'applied', label: 'Applied' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
  { value: 'blocked', label: 'Blocked' },
];

type AssignmentField =
  | 'departmentId'
  | 'jobTitleId'
  | 'gradeId'
  | 'positionId'
  | 'reportingManagerUserId';

const EMPTY_ASSIGNMENT: Record<AssignmentField, string> = {
  departmentId: '',
  jobTitleId: '',
  gradeId: '',
  positionId: '',
  reportingManagerUserId: '',
};

const TYPE_HINTS: Record<MovementType, string> = {
  transfer: 'Move the employee to another department or manager.',
  promotion: 'Raise the job title, grade, or position.',
  demotion: 'Lower the job title, grade, or position.',
  salary_revision: 'Change base salary or hourly rate.',
};

const PROMOTION_FIELDS_REQUIRED =
  'Choose a new job title, grade, or position. A promotion or demotion must change at least one of them.';

/** Draft form for every movement type. Mounted only while open, so it starts clean each time. */
function NewMovementModal({
  companyIds,
  showCompany,
  onClose,
  onCreated,
}: {
  companyIds?: string[];
  showCompany: boolean;
  onClose: () => void;
  onCreated: (created: { id: string; companyId: string }) => void;
}): React.ReactElement {
  const { can } = usePermissions();
  // Pay is `hr:directory` data: without it the type is not offered at all.
  const canRevisePay = can('hr', 'directory');
  const canReadStructure = can('hr', 'structure');
  const [type, setType] = useState<MovementType>('transfer');
  const [person, setPerson] = useState<HrPersonRef | null>(null);
  const companyId = person?.companyId ?? '';
  const employeeId = person?.userId ?? '';
  const [assignment, setAssignment] = useState(EMPTY_ASSIGNMENT);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [comment, setComment] = useState('');
  const [compensationType, setCompensationType] = useState<CompensationType>('salaried');
  const [amount, setAmount] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [correction, setCorrection] = useState(false);

  const typeOptions = (Object.keys(MOVEMENT_TYPE_LABELS) as MovementType[]).filter(
    (value) => value !== 'salary_revision' || canRevisePay
  );
  const isRankChange = type === 'promotion' || type === 'demotion';
  const isPayChange = type === 'salary_revision';

  const { data: directory } = useQuery({
    queryKey: ['hr-movements-directory', companyId],
    queryFn: () => listCompanyEmployments(companyId, 1, 1),
    enabled: companyId !== '',
  });
  const { data: structure } = useQuery({
    queryKey: ['hr-structure', companyId],
    queryFn: () => listHrStructure(companyId),
    enabled: canReadStructure && companyId !== '',
  });
  const { data: profile } = useQuery({
    queryKey: ['hr-employee-profile', companyId, employeeId, undefined],
    queryFn: () => getCompanyEmploymentProfile(companyId, employeeId),
    enabled: isPayChange && canRevisePay && employeeId !== '',
  });

  const structureRows = Array.isArray(structure) ? structure : [];
  const facets = directory?.facets;
  // Structure is the complete catalogue; directory facets are the fallback for
  // callers who can move people but not administer the structure.
  const optionsFor = (
    kind: HrStructureKind,
    fallback: Array<{ id: string; name: string }> | undefined,
    placeholder: string
  ): Array<{ value: string; label: string }> => {
    const fromStructure = structureRows
      .filter((row) => row.kind === kind && row.active !== false)
      .map((row) => ({ value: row._id, label: row.name }));
    const list =
      fromStructure.length > 0
        ? fromStructure
        : (Array.isArray(fallback) ? fallback : []).map((facet) => ({
            value: facet.id,
            label: facet.name,
          }));
    return [{ value: '', label: placeholder }, ...list];
  };

  const setField = (field: AssignmentField) => (value: string) => {
    setAssignment((current) => ({ ...current, [field]: value }));
    setFormError(null);
  };

  const amountNumber = Number(amount);
  const amountValid = amount.trim() !== '' && Number.isFinite(amountNumber) && amountNumber > 0;
  const ready =
    employeeId !== '' &&
    effectiveFrom !== '' &&
    (type === 'transfer'
      ? assignment.departmentId !== '' || assignment.reportingManagerUserId !== ''
      : isPayChange
        ? amountValid
        : true);

  const create = useMutation({
    mutationFn: (input: CreateMovementInput) => createMovement(companyId, input),
    onSuccess: (movement) => {
      toast.success('Job change drafted');
      onCreated({ id: movement._id, companyId });
    },
    onError: (mutationError) => {
      // Server rules (e.g. a rank change that changes nothing) stay visible in the form.
      const message = getErrorMessage(mutationError);
      setFormError(message);
      toast.error(message);
    },
  });

  const handleSubmit = (): void => {
    if (!ready) return;
    if (isRankChange && !assignment.jobTitleId && !assignment.gradeId && !assignment.positionId) {
      setFormError(PROMOTION_FIELDS_REQUIRED);
      return;
    }
    const pick = (fields: AssignmentField[]): CreateMovementInput['after'] =>
      Object.fromEntries(fields.filter((f) => assignment[f] !== '').map((f) => [f, assignment[f]]));
    const input: CreateMovementInput = {
      type,
      userId: employeeId,
      effectiveFrom: new Date(effectiveFrom).toISOString(),
      after: isPayChange
        ? {}
        : isRankChange
          ? pick(['jobTitleId', 'gradeId', 'positionId', 'departmentId'])
          : pick(['departmentId', 'reportingManagerUserId']),
    };
    if (comment.trim()) input.comment = comment.trim();
    if (correction && !isPayChange) input.correction = true;
    if (isPayChange) {
      input.compensation =
        compensationType === 'hourly'
          ? { compensationType, hourlyRate: amountNumber }
          : { compensationType, baseSalary: amountNumber };
    }
    setFormError(null);
    create.mutate(input);
  };

  const currentPay =
    isPayChange && profile?.canViewCompensation && profile.compensation
      ? formatCompensation({
          compensationType: profile.compensation.compensationType ?? 'salaried',
          baseSalary: profile.compensation.baseSalary,
          hourlyRate: profile.compensation.hourlyRate,
          currency: profile.compensation.currency,
        })
      : null;

  return (
    <Modal open onClose={onClose} title="New job change">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          This starts as a draft. Nothing changes until you submit it and it is approved.
        </p>
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
            What is changing?
          </legend>
          <div className="grid grid-cols-2 gap-2">
            {typeOptions.map((value) => {
              const selected = type === value;
              return (
                <label
                  key={value}
                  className={
                    selected
                      ? 'flex cursor-pointer flex-col rounded-xl has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary border border-primary/50 bg-primary/5 px-3 py-2.5 ring-2 ring-primary/15 dark:bg-primary/10'
                      : 'flex cursor-pointer flex-col rounded-xl has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary border border-gray-200 px-3 py-2.5 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                  }
                >
                  <input
                    type="radio"
                    name="movement-type"
                    value={value}
                    checked={selected}
                    onChange={() => {
                      setType(value);
                      setFormError(null);
                    }}
                    className="sr-only"
                  />
                  <span
                    className={
                      selected
                        ? 'text-sm font-medium text-primary dark:text-sky-400'
                        : 'text-sm font-medium text-gray-900 dark:text-gray-100'
                    }
                  >
                    {MOVEMENT_TYPE_LABELS[value]}
                  </span>
                  <span className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {TYPE_HINTS[value]}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        <EmployeePicker
          id="movement-employee"
          companyIds={companyIds}
          showCompany={showCompany}
          value={person}
          onChange={(next) => {
            setPerson(next);
            // Departments and managers belong to the person's company.
            setAssignment(EMPTY_ASSIGNMENT);
            setFormError(null);
          }}
        />

        {isRankChange && (
          <div className="space-y-4">
            <Select
              id="movement-job-title"
              label="New job title"
              value={assignment.jobTitleId}
              onChange={setField('jobTitleId')}
              options={optionsFor('job_title', facets?.jobTitles, 'Keep current job title')}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id="movement-grade"
                label="New grade"
                value={assignment.gradeId}
                onChange={setField('gradeId')}
                options={optionsFor('grade', undefined, 'Keep current grade')}
              />
              <Select
                id="movement-position"
                label="New position"
                value={assignment.positionId}
                onChange={setField('positionId')}
                options={optionsFor('position', undefined, 'Keep current position')}
              />
            </div>
          </div>
        )}

        {!isPayChange && (
          <Select
            id="movement-department"
            label={isRankChange ? 'New department (optional)' : 'New department'}
            value={assignment.departmentId}
            onChange={setField('departmentId')}
            options={optionsFor('department', facets?.departments, 'Keep current department')}
          />
        )}

        {type === 'transfer' && (
          <Combobox
            key={companyId}
            id="movement-manager"
            label="New reporting manager"
            placeholder="Keep current manager"
            disabled={companyId === ''}
            value={assignment.reportingManagerUserId}
            onChange={setField('reportingManagerUserId')}
            loadOptions={async (query) =>
              (await searchCompanyPeople(companyId)(query)).filter(
                (option) => option.value !== employeeId
              )
            }
          />
        )}

        {isPayChange && (
          <div className="space-y-4">
            {currentPay && (
              <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
                Current pay: <span className="font-medium">{currentPay}</span>
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id="movement-compensation-type"
                label="Compensation type"
                value={compensationType}
                clearable={false}
                onChange={(value) =>
                  setCompensationType(value === 'hourly' ? 'hourly' : 'salaried')
                }
                options={[
                  { value: 'salaried', label: 'Salaried' },
                  { value: 'hourly', label: 'Hourly' },
                ]}
              />
              <Input
                id="movement-amount"
                label={compensationType === 'hourly' ? 'New hourly rate' : 'New base salary'}
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
          </div>
        )}

        <Input
          id="movement-effective"
          label="Effective date"
          type="date"
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
        />
        {!isPayChange && (
          <div>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={correction}
                onChange={(event) => setCorrection(event.target.checked)}
                aria-describedby="movement-correction-hint"
              />
              Correct earlier history
            </label>
            <p
              id="movement-correction-hint"
              className="ml-6 text-xs text-gray-600 dark:text-gray-300"
            >
              For a past date before the latest applied change. Later changes are kept, and the
              records they replace stay in history.
            </p>
          </div>
        )}
        <Input
          id="movement-comment"
          label="Comment (optional)"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
        {formError && (
          <p className="text-sm text-danger" role="alert">
            {formError}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!ready || create.isPending}
            loading={create.isPending}
            onClick={handleSubmit}
          >
            Create draft
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function CompanyMovementsTab({ companyIds, showCompany }: HrAreaProps): React.ReactElement {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '');
  const [selected, setSelected] = useState<{ id: string; companyId: string } | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  useOpenFromQuery(setIsCreateOpen);
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  // Emergency bypass needs organization-wide authority, which the server
  // verifies independently; this only decides whether to offer the control.
  const canBypass = can('organization', 'update') || can('users', 'manage_roles');

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['hr-movements', companyIds?.join(',') ?? '', page, status],
    queryFn: () => listHrMovements(companyIds, { page, limit: 25, status: status || undefined }),
    placeholderData: keepPreviousData,
  });

  const hasMore = data ? page * data.limit < data.total : false;
  const rows = Array.isArray(data?.data) ? data.data : [];

  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-full sm:w-44">
          <Select
            id="movement-status"
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={STATUS_OPTIONS}
          />
        </div>
        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          New job change
        </Button>
      </div>

      <div className="mt-4" aria-live="polite">
        {isLoading ? (
          <div className="flex justify-center py-10" role="status">
            <Spinner />
            <span className="sr-only">Loading movements</span>
          </div>
        ) : error ? (
          <div className="space-y-3" role="alert">
            <p className="text-sm text-danger">{getErrorMessage(error)}</p>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {status
              ? 'No job change matches this status.'
              : 'No job changes yet. Start one to move, promote or change the pay of someone.'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-3 py-2">
                      Employee
                    </th>
                    {showCompany && (
                      <th scope="col" className="hidden px-3 py-2 lg:table-cell">
                        Company
                      </th>
                    )}
                    <th scope="col" className="px-3 py-2">
                      Change
                    </th>
                    <th scope="col" className="hidden px-3 py-2 sm:table-cell">
                      Status
                    </th>
                    <th scope="col" className="hidden px-3 py-2 md:table-cell">
                      Effective
                    </th>
                    <th scope="col" className="px-3 py-2">
                      <span className="sr-only">Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((movement) => (
                    <tr key={movement._id}>
                      <td className="px-3 py-3">
                        <span className="block font-medium text-gray-900 dark:text-gray-100">
                          {movement.employee?.name ?? 'Unknown employee'}
                        </span>
                        <span className="block text-xs text-gray-600 dark:text-gray-300">
                          {movement.employee?.employeeId}
                          {showCompany && movement.companyName && (
                            <span className="lg:hidden"> · {movement.companyName}</span>
                          )}
                        </span>
                      </td>
                      {showCompany && (
                        <td className="hidden px-3 py-3 text-gray-700 dark:text-gray-200 lg:table-cell">
                          {movement.companyName ?? '—'}
                        </td>
                      )}
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-200">
                        <span className="block">{movementTypeLabel(movement.type)}</span>
                        <span className="block text-xs text-gray-600 dark:text-gray-300">
                          {movementChangeSummary(movement.changedFields)}
                        </span>
                        <span className="mt-1 block sm:hidden">
                          <MovementStatusBadge status={movement.status as MovementStatus} />
                        </span>
                      </td>
                      <td className="hidden px-3 py-3 sm:table-cell">
                        <MovementStatusBadge status={movement.status as MovementStatus} />
                      </td>
                      <td className="hidden px-3 py-3 text-gray-700 dark:text-gray-200 md:table-cell">
                        {formatDate(movement.effectiveFrom)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setSelected({ id: movement._id, companyId: movement.companyId })
                          }
                        >
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1 || isFetching}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasMore || isFetching}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>

      {selected && (
        <MovementDetail
          key={selected.id}
          companyId={selected.companyId}
          movementId={selected.id}
          canBypass={canBypass}
          onClose={() => setSelected(null)}
        />
      )}

      {isCreateOpen && (
        <NewMovementModal
          companyIds={companyIds}
          showCompany={showCompany}
          onClose={() => setIsCreateOpen(false)}
          onCreated={(created) => {
            void queryClient.invalidateQueries({ queryKey: ['hr-movements'] });
            setIsCreateOpen(false);
            setSelected(created);
          }}
        />
      )}
    </Card>
  );
}
